import {
  type FC, type ReactNode, useCallback, useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  ButtonComponent, ButtonSize,
} from '~/_shared/baseComponents/buttons';
import { TextInputComponent } from '~/_shared/baseComponents/inputs';
import { SliderWithValueLabelsComponent } from '~/_shared/components/slider/sliderWithValueLabels/sliderWithValueLabels.component';
import {
  TextInputWithGeolocationComponent, type TextInputWithGeolocationProps,
} from '~/_shared/components/textInputWithGeolocation/textInputWithGeolocation.compontent';
import { useTranslation } from '~/_shared/utils/hooks';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { UnitDropdownComponent } from '~/proximity/panel/form/formFields/unitDropdown.component';
import type { PlaceSearchListingItem } from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.container';
import {
  buttonStyle, distanceStyle, exampleStyle, formStyle, hideStyle, labelStyle, placesToolAlertStyle, settingStyle, sliderStyle, unitStyle,
} from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.styles';
import type { PlacesDistance } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { useMapSettingsSettingsUnitSystemSelector } from '~/store/mapSettings/settings/mapSettingsSettings.selectors';
import { Trans } from '~/translations/Trans';
import { MapToolWrapperComponent } from '../../mapToolWrapper/mapToolWrapper.component';
import { PlacesToolListingContainer } from './placesToolListing.container';

export type PlacesSearchProps = Readonly<{
  searchField: string;
  distance: PlacesDistance;
}>;

type PlacesToolComponentProps = Readonly<{
  error: ReactNode;
  geolocationInputProps: TextInputWithGeolocationProps;
  isFormLoading: boolean;
  isAddressDataValid: boolean;
  isLegendMode: boolean;
  searches: ReadonlyArray<PlaceSearchListingItem>;
  searchField?: string;

  createSearch: (searchProps: PlacesSearchProps) => void;
}>;

export const PlacesToolComponent: FC<PlacesToolComponentProps> = ({
  error,
  createSearch,
  isLegendMode,
  isFormLoading,
  isAddressDataValid,
  geolocationInputProps,
  searches,
}: PlacesToolComponentProps) => {
  const [t] = useTranslation();
  const defaultUnitSystem = useMapSettingsSettingsUnitSystemSelector();

  const [searchField, setSearchField] = useState('');
  const [searchDistance, setSearchDistance] = useState<number | null>(15);
  const [distanceUnit, setDistanceUnit] = useState(defaultUnitSystem);

  const handleSearch = useCallback(() => {
    if (!searchField || isNullsy(searchDistance)) {
      return;
    }

    createSearch({
      distance: { value: searchDistance, unit: distanceUnit },
      searchField,
    });

    setSearchField('');
  }, [createSearch, distanceUnit, searchDistance, searchField]);

  const setDistanceFromText = useCallback((distanceText: string) =>
    setSearchDistance(() => {
      const distance = parseFloat(distanceText);
      if (isNaN(distance)) {
        return null;
      }

      return distance;
    }), []);

  const handleDistanceChange = useCallback(([distance]: [number]) =>
    setSearchDistance(distance), []);

  const isFormValid = isAddressDataValid && !isFormLoading && searchField.trim().length > 0;

  return (
    <MapToolWrapperComponent>
      <div>

        {!isLegendMode && (
          <div css={formStyle}>
            <div css={settingStyle}>
              <span css={labelStyle}>
                <Trans
                  components={{ example: <span css={exampleStyle} /> }}
                  tOptions={{
                    placeholder: t('placesTool.searchField.example'),
                  }}
                  i18nKey="placesTool.step1.label"
                />
              </span>
              <TextInputComponent
                onChange={setSearchField}
                placeholder={t('placesTool.step1.placeholder')}
                value={searchField}
              />
            </div>

            <div css={settingStyle}>
              <span css={labelStyle}>
                <Trans
                  components={{ example: <span css={exampleStyle} /> }}
                  i18nKey="placesTool.step2.label"
                />
              </span>
              <TextInputWithGeolocationComponent
                placeholder={t('placesTool.step2.placeholder')}
                {...geolocationInputProps}
              />
            </div>

            <div css={settingStyle}>
              <span css={labelStyle}>
                {t('placesTool.step3.label')}
              </span>

              <span css={distanceStyle}>
                <SliderWithValueLabelsComponent
                  css={sliderStyle}
                  handleLabelStyleCommon={hideStyle}
                  max={50}
                  min={1}
                  onChange={handleDistanceChange}
                  value={[searchDistance ?? 0]}
                  valueSuffix={` ${t(distanceUnit)}`}
                />
                <UnitDropdownComponent
                  hideIcon
                  inputStyle={unitStyle}
                  onChange={setDistanceFromText}
                  onUnitChange={setDistanceUnit}
                  unit={distanceUnit}
                  value={searchDistance?.toString() ?? ''}
                />
              </span>
            </div>

            <ButtonComponent
              css={buttonStyle}
              onClick={isFormValid ? handleSearch : undefined}
              size={ButtonSize.Large}
              text={t('Search')}
              isDisabled={!isFormValid}
            />

            {error && (
              <AlertComponent
                size="small"
                type="danger"
                css={placesToolAlertStyle}
              >
                {error}
              </AlertComponent>
            )}

          </div>
        )}

        <PlacesToolListingContainer
          searches={searches}
        />

      </div>
    </MapToolWrapperComponent>
  );
};
